import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Moment } from 'moment-timezone';
import { BehaviorSubject, Observable, of, Subject, take } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { ConnectAccountStatusResponse, StripeConnectService } from './stripe-connect.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { PlaidToken } from '../../models/entities/plaid-token';
import { OrganizationBroadcastService, OrganizationEventType } from '../organization/organization-broadcast.service';

export type ConnectAccountStatus = ConnectAccountStatusResponse['status'];

/** Stripe's two outstanding-requirement lists, carried together because they are read together. */
export interface ConnectAccountRequirements {
    currentlyDue: string[];
    pendingVerification: string[];
}
export type TreasuryDisplayStatus = ConnectAccountStatus | 'error' | 'forbidden';

/** What underwriting has released, as the underwriting-status endpoint reports it. */
export interface TreasuryUnderwritingStatus {
    status: string | null;
    expedited: boolean;
    spendCardsEnabled: boolean;
    outboundTransfersEnabled: boolean;
    inboundTransfersEnabled: boolean;
}

/** The second-underwriting queue statuses (core `SecondUnderwritingStatuses`). */
export const SECOND_UNDERWRITING_STATUSES = {
    PENDING: 'pending',
    APPROVED: 'approved',
    DISMISSED: 'dismissed',
    REJECTED: 'rejected',
    ORGANIZATION_DELETED: 'organization_deleted',
} as const;

/** The three capabilities second underwriting releases, each the flag the server refuses on. */
export type TreasuryUnderwritingCapability = 'spendCardsEnabled' | 'outboundTransfersEnabled' | 'inboundTransfersEnabled';

/**
 * SD-10162: is this capability held back only because the org's second underwriting is still to
 * come — off, while the org is in the queue or not yet queued? That is when the Approval Required
 * dialog answers the attempt.
 *
 * Every other way a capability can be off is NOT a review in progress: a super switching it off on an
 * approved org, or a dismissed org. Those keep their own messaging, so this says false for them.
 * An unread status (null) is false too — the server still refuses whatever is not released.
 */
export function awaitsSecondUnderwriting(
    status: TreasuryUnderwritingStatus | null,
    capability: TreasuryUnderwritingCapability,
): boolean {
    if (!status || status[capability]) {
        return false;
    }

    return status.status === null || status.status === SECOND_UNDERWRITING_STATUSES.PENDING;
}

export interface TreasuryAccountInfo {
    id: number;
    bankAccountId: number | null;
    stripeFinancialAccountId: string;
    status: 'open' | 'closed';
    friendlyName?: string;
}

export interface TreasuryBalance {
    available: number;
    pending: number;
    outboundPending: number;
    totalPending: number;
    currency: string;
}

export interface TreasuryFinancialAddresses {
    aba?: {
        routingNumber: string;
        accountNumber: string;
        accountNumberLast4: string;
        bankName: string;
        accountHolderName: string;
    };
}

export interface PendingItemsSummary {
    count: number;
    total: number;
}

export interface TreasuryBalanceResponse {
    treasury: TreasuryAccountInfo | null;
    balance: TreasuryBalance | null;
    financialAddresses: TreasuryFinancialAddresses | null;
    pendingItems?: PendingItemsSummary | null;
    failedItems?: PendingItemsSummary | null;
    onboardingRequired?: boolean;
    lastUpdatedAt: string | null;
}

export interface RecentActivityTransaction {
    id: string;
    type: string;
    typeLabel: string;
    description: string;
    detail: string | null;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    isCredit: boolean;
    isInboundTransferReversal?: boolean;
    createdAt: string;
    linkedTransactionId: string | null;
    disputeStatus: string | null;
    disputeFiledAt: string | null;
    disputeResolvedAt: string | null;
}

export interface RecentActivityResponse {
    transactions: RecentActivityTransaction[];
    hasMore: boolean;
    viewAllUrl: string;
}

export interface PendingTransfer {
    id: number;
    type: string;
    typeLabel: string;
    description: string;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    isCredit: boolean;
    createdAt: string;
    /**
     * Stripe's live cancel window for an inbound transfer; null for every other row type and
     * whenever the backend could not determine it. Drives the Cancel action, render it only
     * on an explicit `true`, never off `status`. See SD-8675.
     */
    cancelable: boolean | null;
    fundsAvailable: string | null;
    failureCode: string | null;
    failureMessage: string | null;
    returnCode: string | null;
    returnMessage: string | null;
}

export interface PendingTransfersResponse {
    data: PendingTransfer[];
    failed: PendingTransfer[];
}

export interface StatisticsBreakdownItem {
    accountCode: string;
    accountName: string;
    /**
     * True when the row's category has been soft-deleted. The name arrives plain and the
     * marker is rendered client-side (SD-9296) — the legend truncates the name to a fixed
     * width, so its chip has to sit outside the truncating element, and the donut tooltip
     * puts the marker on its own line.
     *
     * Optional on purpose: an API that predates the core half of SD-9296 omits it, and this
     * repo cannot guarantee deploy order. Absent reads as "not deleted", so the marker simply
     * doesn't render — declaring it required would let TypeScript promise a boolean the wire
     * may not carry.
     */
    isDeleted?: boolean;
    amount: number;
    percentage: number;
}

export interface StatisticsResponse {
    totalRevenue: number;
    currency: string;
    period: string;
    breakdown: StatisticsBreakdownItem[];
}

export interface StatisticsParams {
    period: 'this_month' | 'last_month' | 'custom';
    startDate?: string;
    endDate?: string;
}

export interface TransactionDetailAuditEvent {
    event: string;
    timestamp: string;
    actor: string;
}

export interface TransactionDetailBalanceImpact {
    cash: number;
    inboundPending: number;
    outboundPending: number;
}

export interface TransactionDetail {
    id: number;
    stripeTransactionId: string;
    type: string;
    typeLabel: string;
    status: string;
    statusLabel: string;
    amount: number;
    currency: string;
    description: string;
    isCredit: boolean;
    effectiveAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    balanceImpact: TransactionDetailBalanceImpact | null;
    flowDetails: Record<string, any> | null;
    metadata: Record<string, any> | null;
    auditTrail: TransactionDetailAuditEvent[];
    ledgerTransactionId: number | null;
}

export interface PendingTransferAlert {
    direction: 'inbound' | 'outbound';
    amountCents: number;
}

export interface ConnectedBankAccount {
    id: number;
    friendlyName: string;
    last4: string;
    routingNumber: string | null;
    balance: number | null;
    /**
     * SD-8671 review follow-up (F-73c4): how the account was linked, which decides whether a bank
     * balance is even OBTAINABLE for it.
     *
     * Required on this interface because ONE list feeds two surfaces — the Move Money pickers and the
     * Financial Accounts card — and admitting `manual` rows (the fix this ticket exists for) gave the
     * card a row it had no correct rendering for: `isBalanceLinked` cannot express "a balance can
     * never arrive", only "one has not arrived yet", so a manual account either drew a "Link account"
     * CTA the API rejects or an indefinite "--". See `bankBalanceIsUnobtainable()`.
     */
    sourceType: string | null;
    /**
     * SD-9792: core's `getIsAllianceAccountAttribute()`. Server state, not re-derivable here — it
     * also requires the org to hold a matching AllianceCredential, which the client cannot see.
     * Read by `bankBalanceIsUnobtainable()`.
     */
    isAllianceAccount: boolean;
    /**
     * SD-11249 (review F-6632): does this row render its PLAID balance? Read by
     * `balanceStillExpected()`. A Plaid balance arrives on core's Plaid import, not from anything
     * this dashboard can trigger, so such a row must not be polled for — the same reasoning as the
     * Alliance carve-out beside it.
     *
     * OPTIONAL on purpose, like every other field this ticket added: a caller that does not supply
     * it reads `undefined`, the carve-out does not fire, and the poll behaves exactly as before.
     *
     * SD-11604: also read by the shared balance-slot helpers (`PlaidRenderingBankAccount`), so the
     * old Stripe marker on a bank switched to Plaid drives no "paused" or "Connection ended" copy.
     */
    rendersPlaidBalance?: boolean;
    /**
     * SD-11604 (Rule 4): the Plaid link, carried so a Plaid-reading row whose Plaid login has
     * expired can offer the Plaid relink here, as the Transactions page already does. Without it
     * this card had no Plaid error state at all, and the only Reconnect it could draw was Stripe's.
     *
     * Optional, absent when the caller did not load the relation, which reads as "no Plaid error".
     */
    plaidToken?: PlaidToken | null;
    isBalanceLinked: boolean;
    /**
     * SD-10133: core's `getIsConnectionTerminalAttribute()` — can re-linking THIS connection ever
     * work, or is the Stripe object gone/severed/permanently missing the permission we need?
     *
     * A fifth state, and one `isBalanceLinked` cannot express. That flag says a human must act; it
     * cannot say WHICH action, and for these rows the only action that works is linking a NEW
     * account. Five production rows spent 41 days being offered a Reconnect button that could not
     * succeed, which is worse than offering nothing — it invites a customer to repeat an action
     * that cannot work.
     *
     * Server state and NOT re-derivable here: it turns on `fc_reconnect_reason`, which the client
     * never receives. Nullable so "core did not say" stays distinct from a real `false`, and the
     * consumer must treat null as NOT terminal — a UI bundle running against a core predating the
     * append then behaves exactly as it does today.
     */
    isConnectionTerminal: boolean | null;
    /**
     * SD-9632: whether the figure in `balance` above is one the BANK actually reported, as opposed
     * to one we never retrieved.
     *
     * A fourth state, and the one this interface could not express. `isBalanceLinked` says whether a
     * human must re-link and `balance === null` says whether anything arrived, but neither separates
     * a true $0.00 from a zero core stored because Stripe had not computed a balance yet, so the
     * card rendered a confident "$0.00" for 12 live orgs, one of them a reserve account.
     *
     * ⚠️ NOT a straight pass-through of core's `getIsBalanceConfirmedAttribute()`, and this comment
     * used to imply it was (review F-40d2). Core's flag describes the Financial Connections figure
     * ONLY, while `balance` here is `fcBalance ?? plaidBalance`. `StripeTreasuryComponent` therefore
     * scopes it at the mapping: once the Plaid fallback supplies `balance`, this reports `true`,
     * because core's answer is not about the number being carried and must not suppress it.
     *
     * Server state and NOT re-derivable here: core's answer turns on Stripe's `balance.as_of`, which
     * only core holds. Nullable so "core did not say" stays distinct from a real `false`; see
     * `bankBalanceIsUnconfirmed()` for why that default has to fail toward showing the figure.
     */
    isBalanceConfirmed: boolean | null;
    /**
     * SD-9119: core's terminal marker for a Financial Connections account whose connection was
     * severed at the bank (`fc_reconnect_required_at`). Server state, not UI state, it is what
     * distinguishes "Reconnect" from "Link account", and without it on this interface the dashboard
     * could only render the first-time-link copy for an account the admin had already linked.
     */
    fcReconnectRequiredAt: Moment | null;
    /**
     * SD-9845: when core last successfully refreshed the balance above from Stripe. Server state,
     * not derivable here, and the only thing that distinguishes a current figure from one frozen
     * when the feed died — which is the state SD-9584 creates by design whenever it withholds the
     * reconnect CTA for a transient `inactive` connection. Read by
     * `bankBalanceUpdatesPausedTooltip()`.
     */
    fcBalanceRefreshedAt: Moment | null;
    /**
     * SD-10504: Stripe's own last word on the connection ('active', 'inactive', 'disconnected').
     *
     * ⚠️ It LAGS by up to the hourly import, so it is disclosure only — the delete guard re-reads
     * the connection at Stripe server-side and is the authority on whether a removal is refused.
     */
    fcStatus: string | null;
    /**
     * SD-10744: Stripe's verdict on an inactive connection (`relink_required`, `none`, …) and why.
     * Null when healthy. `none` means no customer action at the bank will revive it, which is what
     * puts the contact-support notice after a failed reconnect (SD-10334 option B).
     */
    fcStatusDetailsAction?: string | null;
    fcStatusDetailsCause?: string | null;
    /**
     * SD-10504: when the connection last came back.
     *
     * ⚠️ NULL does NOT mean stale — it is null on ~71% of live connected rows because those
     * connections never broke, so it must never be read as evidence a connection is dead.
     */
    fcLastRecoveredAt: Moment | null;
    stripeFinancialConnectionsId: string | null;
    stripePaymentMethodId: string | null;
    /**
     * SD-8671: core's answer to "can PayHOA actually move money on this account?", the single
     * signal the transfer pickers key on. Server state, computed by
     * UnifiedBankAccount::getIsTransferEligibleAttribute() from the same two predicates the
     * inbound and outbound transfer guards enforce, so what the picker shows and what a transfer
     * accepts cannot drift apart. The transfer page previously inferred this from the presence of
     * `stripePaymentMethodId`/`stripeFinancialConnectionsId` and silently dropped every account
     * that had neither — including a cohort of accounts Stripe holds only as a `ba_`, measured at 17
     * live rows on 2026-08-19 (provenance: core's
     * `FinancialConnectionsService::findAdoptableExternalAccount()`, which carries the query).
     */
    isTransferEligible: boolean;
    /**
     * SD-9540: the same question for the "To" side only, which is a weaker requirement.
     *
     * `isTransferEligible` governs both directions and is what the "From" picker must keep using.
     * This one additionally admits a Connect-created external account Stripe holds only as a `ba_`,
     * because outbound accepts a BankAccount id directly and needs no mandate — whereas debiting
     * such an account inbound does. Two server-computed flags rather than one, because a single flag
     * cannot say "TO yes, FROM no", and that is why these accounts were dropped from both dropdowns
     * instead of one. Do not re-derive either from raw Stripe ids here; that inference is the defect.
     */
    isOutboundEligible: boolean;
    /**
     * SD-11585: core's `canEnableInboundTransfers` — may this row be offered the bank sign-in that
     * would let money move INTO Treasury from it?
     *
     * A third server-computed flag for the same reason there are already two: neither of the ones
     * above can express it. `isTransferEligible` says the account cannot be debited TODAY and says
     * nothing about whether that is fixable, and `isBalanceLinked` answers a BALANCE question that
     * reads "nothing to do here" on exactly these rows — which is why they were offered no action
     * at all once SD-11497 taught them to render their Plaid balance.
     *
     * ⚠️ NOT RE-DERIVABLE HERE, and the `?? false` fallback at the mapping site is deliberate. The
     * endpoint behind the action validates on core's `linkableByFinancialConnections()`, so a
     * client-side inference would eventually offer a CTA the API refuses — the precise drift
     * SD-10776 recorded. Against a core that does not send the field the action simply never
     * appears, which is exactly how the page behaved before this ticket.
     */
    canEnableInboundTransfers?: boolean;
    /** Set while Stripe is waiting for the two small deposits to be confirmed (SD-9078). */
    microdepositVerificationRequiredAt: Moment | null;
    // SD-8605: no transient UI state on this interface. These objects are rebuilt from scratch by
    // `fetchConnectedAccounts()` on every balance-poll tick, so anything in-flight stored here is
    // silently wiped mid-journey. In-flight state belongs on the component (see
    // `linkingAccountId` in dashboard.component.ts and totals-box.component.ts).
}

/**
 * SD-11999: the answer to "can this organization pay a vendor by ACH", plus whether the question
 * could be answered at all. `unreadable` exists so a failed read is never mistaken for a "no".
 */
export interface PayableAchAvailability {
    hasTreasuryAccount: boolean;
    unreadable: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TreasuryService {
    /** Current Connect account onboarding status */
    private readonly _connectStatus$ = new BehaviorSubject<TreasuryDisplayStatus | null>(null);
    readonly connectStatus$ = this._connectStatus$.asObservable();

    /**
     * SD-10156: the org's `acct_` id, populated from the same status response and therefore
     * super-only by the server's decision — null for everyone else and for older cores.
     */
    private readonly _connectAccountId$ = new BehaviorSubject<string | null>(null);
    readonly connectAccountId$ = this._connectAccountId$.asObservable();

    /**
     * What Stripe still wants from this account, as the status response reports it: the surfaces
     * that ask a customer to finish verification can then name the steps Stripe will ask for
     * rather than a list written by hand. Null until a status has been read, and after a failed
     * one — a status we could not read tells us nothing about what is outstanding.
     */
    private readonly _connectRequirements$ = new BehaviorSubject<ConnectAccountRequirements | null>(null);
    readonly connectRequirements$ = this._connectRequirements$.asObservable();

    /** Treasury balance and account data from the balance endpoint */
    private readonly _balanceResponse$ = new BehaviorSubject<TreasuryBalanceResponse | null>(null);
    readonly balanceResponse$ = this._balanceResponse$.asObservable();

    /** Recent activity transactions for the balance card */
    private readonly _recentActivity$ = new BehaviorSubject<RecentActivityResponse | null>(null);
    readonly recentActivity$ = this._recentActivity$.asObservable();

    /** Connected (external) bank accounts linked via Financial Connections */
    private readonly _connectedAccounts$ = new BehaviorSubject<ConnectedBankAccount[] | null>(null);
    readonly connectedAccounts$ = this._connectedAccounts$.asObservable();

    /** Emits when the user requests to resume/start verification */
    private readonly _resumeVerification$ = new Subject<void>();
    readonly resumeVerification$ = this._resumeVerification$.asObservable();

    /**
     * SD-11542: true from a verification request until its next screen opens or it settles, so the
     * button that asked can show it is working through the reads that come before Stripe's form.
     */
    private readonly _verificationStarting$ = new BehaviorSubject<boolean>(false);
    readonly verificationStarting$ = this._verificationStarting$.asObservable();

    /** Emits when connected accounts should be re-fetched (e.g., after linking a new account) */
    private readonly _refreshConnectedAccounts$ = new Subject<void>();
    readonly refreshConnectedAccounts$ = this._refreshConnectedAccounts$.asObservable();

    /** Pending transfer alert shown on the dashboard after submitting a transfer */
    private readonly _pendingTransferAlert$ = new BehaviorSubject<PendingTransferAlert | null>(null);
    readonly pendingTransferAlert$ = this._pendingTransferAlert$.asObservable();

    /** Emits when the dashboard should re-fetch balance + activity (e.g., after websocket event) */
    private readonly _refreshDashboardData$ = new Subject<void>();
    readonly refreshDashboardData$ = this._refreshDashboardData$.asObservable();

    /** Track which org's connect status is cached to avoid stale cross-org data */
    private _connectStatusOrgId: number | null = null;

    /** Whether a prefetch API call is currently in flight */
    private _prefetchInFlight = false;

    constructor(
        private _http: ApplicationHttpClient,
        private _stripeConnectService: StripeConnectService,
        private _orgBroadcast: OrganizationBroadcastService,
    ) {
        // SD-6865 broadcast adoption: receive the live connect-status flip from
        // the backend (dispatched by TreasuryWebhookSubscriber on Stripe's
        // account.updated when treasuryCapability becomes active) and re-fetch
        // so `connectStatus$` re-emits the now-verified value. Replaces the
        // racy one-shot `refreshConnectStatus` call previously attempted in
        // the deposit-account-selector's post-KYC callback.
        this._orgBroadcast.onEventType$(OrganizationEventType.TREASURY_CONNECT_STATUS_UPDATED)
            .subscribe((event) => {
                // Defensive: Echo channels are already scoped to
                // `Organization.{orgId}`, but a replayed or malformed payload
                // could theoretically reference a different org. Drop anything
                // that does not match the current JWT's orgId.
                const jwt = LegFiJwtService.read();
                if (jwt?.orgId !== event.organizationId) {
                    return;
                }
                this._fetchConnectStatus(event.organizationId);
            });
    }

    /**
     * Reset state when entering the Treasury view to prevent stale data
     * from a previous organization leaking across org switches.
     *
     * Connect status is intentionally NOT cleared here — it persists from prefetch, and
     * prefetchConnectStatus() owns when it is re-read (org switch, or a cached 'error', which
     * is never a settled status).
     */
    resetState(): void {
        this._balanceResponse$.next(null);
        this._recentActivity$.next(null);
        this._connectedAccounts$.next(null);
        this._pendingTransferAlert$.next(null);
    }

    /**
     * Prefetch the Connect account status for an organization.
     * Called from the app shell (v2-layout) to eliminate the blocking API call
     * when the user navigates to Treasury.
     */
    prefetchConnectStatus(organizationId: number): void {
        const cached = this._connectStatus$.getValue();

        // Already have status for this org. 'error' does not count as having it: it is written
        // by the client when a status read fails, so no server ever said it, and holding it
        // would answer every later navigation with one failed read for the rest of the session.
        // 'forbidden' is a real 403 the server did return, so it caches like any genuine status.
        if (this._connectStatusOrgId === organizationId && cached !== null && cached !== 'error') {
            return;
        }

        // Already fetching for this org
        if (this._connectStatusOrgId === organizationId && this._prefetchInFlight) {
            return;
        }

        this._fetchConnectStatus(organizationId);
    }

    /**
     * Force a re-fetch of the Connect account status, bypassing the prefetch cache guards.
     * Use after actions that may change KYC state (e.g. Stripe onboarding completion) so
     * subscribers see the latest status without waiting for a navigation or org switch.
     */
    refreshConnectStatus(organizationId: number): void {
        this._fetchConnectStatus(organizationId);
    }

    private _fetchConnectStatus(organizationId: number): void {
        this._connectStatusOrgId = organizationId;
        this._prefetchInFlight = true;
        this._connectStatus$.next(null);
        this._connectAccountId$.next(null);
        this._connectRequirements$.next(null);

        this._stripeConnectService.getConnectAccountStatus(organizationId).pipe(
            take(1),
        ).subscribe({
            next: (response) => {
                this._prefetchInFlight = false;
                if (this._connectStatusOrgId === organizationId) {
                    this._connectStatus$.next(response.status);
                    this._connectAccountId$.next(response.stripeConnectAccountId ?? null);
                    this._connectRequirements$.next({
                        currentlyDue: response.requirementsCurrentlyDue ?? [],
                        pendingVerification: response.requirementsPendingVerification ?? [],
                    });
                }
            },
            error: (err: { status?: number }) => {
                this._prefetchInFlight = false;
                if (this._connectStatusOrgId === organizationId) {
                    this._connectStatus$.next(err?.status === 403 ? 'forbidden' : 'error');
                    this._connectAccountId$.next(null);
                    this._connectRequirements$.next(null);
                }
            },
        });
    }

    /**
     * The connect status as it stands right now, for a decision that cannot wait on the stream:
     * whether backing out of setup should leave the organization where it was.
     */
    get connectStatusSnapshot(): TreasuryDisplayStatus | null {
        return this._connectStatus$.getValue();
    }

    get verificationStartingSnapshot(): boolean {
        return this._verificationStarting$.getValue();
    }

    setVerificationStarting(starting: boolean): void {
        this._verificationStarting$.next(starting);
    }

    /**
     * Update the Connect account onboarding status
     */
    setConnectStatus(status: TreasuryDisplayStatus | null): void {
        this._connectStatus$.next(status);
    }

    /**
     * Update the treasury balance response
     */
    setBalanceResponse(response: TreasuryBalanceResponse | null): void {
        this._balanceResponse$.next(response);
    }

    /**
     * Update the recent activity response
     */
    setRecentActivity(response: RecentActivityResponse | null): void {
        this._recentActivity$.next(response);
    }

    /**
     * Update the connected bank accounts
     */
    setConnectedAccounts(accounts: ConnectedBankAccount[] | null): void {
        this._connectedAccounts$.next(accounts);
    }

    /**
     * Signal that the user wants to resume verification
     */
    resumeVerification(): void {
        this._resumeVerification$.next();
    }

    /**
     * Signal that connected accounts should be re-fetched
     */
    requestConnectedAccountsRefresh(): void {
        this._refreshConnectedAccounts$.next();
    }

    /**
     * Set the pending transfer alert (shown on dashboard after submitting a transfer)
     */
    setPendingTransferAlert(alert: PendingTransferAlert | null): void {
        this._pendingTransferAlert$.next(alert);
    }

    /**
     * Signal that the dashboard should re-fetch balance + activity
     */
    requestDashboardRefresh(): void {
        this._refreshDashboardData$.next();
    }

    /**
     * Gets the treasury balance and account status for an organization.
     * Returns onboardingRequired: true if no treasury account exists.
     */
    public getBalance(organizationId: number): Observable<TreasuryBalanceResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Balance(organizationId),
        );
        return this._http.get<TreasuryBalanceResponse>(url);
    }

    /**
     * Gets the most recent transactions for the balance card.
     */
    public getRecentActivity(organizationId: number): Observable<RecentActivityResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.RecentActivity(organizationId),
        );
        return this._http.get<RecentActivityResponse>(url);
    }

    /**
     * Gets pending/processing transfers for the pending transfers dialog.
     * Pass includeFailed=true to also fetch terminal-state (failed/canceled/returned) transfers.
     */
    public getPendingTransfers(organizationId: number, includeFailed = false): Observable<PendingTransfersResponse> {
        let url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransfers(organizationId),
        );
        if (includeFailed) {
            url += '?include=failed';
        }
        return this._http.get<PendingTransfersResponse>(url);
    }

    /**
     * Dismiss a failed transfer from the attention list.
     */
    public dismissTransfer(organizationId: number, type: string, transferId: number): Observable<void> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransferDismiss(organizationId, type, transferId),
        );
        return this._http.post<void>(url, '');
    }

    /**
     * Retry a failed transfer by creating a new one with the same parameters.
     */
    public retryTransfer(organizationId: number, type: string, transferId: number): Observable<{ data: PendingTransfer }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PendingTransferRetry(organizationId, type, transferId),
        );
        return this._http.post<{ data: PendingTransfer }>(url, '');
    }

    /**
     * Gets revenue statistics for an organization, broken down by GL account category.
     */
    public getStatistics(organizationId: number, params: StatisticsParams): Observable<StatisticsResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Statistics(organizationId),
        );
        let queryParams = new HttpParams().set('period', params.period);
        if (params.startDate) {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.endDate) {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        return this._http.get<StatisticsResponse>(url, { params: queryParams });
    }

    /**
     * Gets the full detail for a single Treasury transaction.
     */
    public getTransactionDetail(organizationId: number, transactionId: number): Observable<TransactionDetail> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.Transaction(organizationId, transactionId),
        );
        return this._http.get<TransactionDetail>(url);
    }

    /**
     * Which money movements underwriting has released, for the surfaces that offer Move Money.
     *
     * A failed read resolves to null rather than an error: the status only decides which options
     * the dialog disables, and refusing to open the dialog because the gate could not be read
     * would be a worse answer than opening it with every option live.
     */
    public getUnderwritingStatus(organizationId: number): Observable<TreasuryUnderwritingStatus | null> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.UnderwritingStatus(organizationId),
        );
        return this._http.get<TreasuryUnderwritingStatus>(url).pipe(
            catchError(() => of(null)),
        );
    }

    /**
     * SD-11999: whether this organization has a Treasury account a vendor can be paid from.
     *
     * The payables dialogs used to answer this from the organization's `depositBankAccounts`
     * include, which the API treats as ADMIN-ONLY, so a user holding Payables write but no admin
     * role was never sent the data and the ACH option was silently hidden. This asks the question
     * directly, on the same permission as the submit it leads to.
     *
     * ⚠️ RETURNS `unreadable` AS WELL AS THE ANSWER, DELIBERATELY. Collapsing every failure into a
     * bare `false` makes "this organization has no Treasury account" and "I could not read the
     * gate" byte-identical on screen: no row, no hint, nothing logged. A ui deploy that lands
     * ahead of the core endpoint would then remove a payment rail from EVERY organization,
     * admins included, and look exactly like normal. Callers show a hint on `unreadable`; the
     * warning below makes an undeployed endpoint visible in the console.
     *
     * Still fails CLOSED: `hasTreasuryAccount` is false whenever the gate cannot be read, because
     * offering ACH on an unreadable gate just moves the error to submit time.
     */
    public hasPayableAchAvailable(organizationId: number): Observable<PayableAchAvailability> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.PayableAchAvailability(organizationId),
        );
        return this._http.get<{ hasTreasuryAccount: boolean }>(url).pipe(
            map((response) => {
                // A body without the flag is not a "no" - it is an answer we do not understand.
                if (typeof response?.hasTreasuryAccount !== 'boolean') {
                    console.warn('[TreasuryService] SD-11999 payable ACH availability response carried no hasTreasuryAccount flag; hiding ACH', response);
                    return { hasTreasuryAccount: false, unreadable: true };
                }
                return { hasTreasuryAccount: response.hasTreasuryAccount, unreadable: false };
            }),
            catchError((error) => {
                console.warn('[TreasuryService] SD-11999 could not read payable ACH availability; hiding ACH', error);
                return of({ hasTreasuryAccount: false, unreadable: true });
            }),
        );
    }

}
